import { z } from 'zod';

import { ShippingRateKind } from '@nucleus-commerce/api-client/admin';
import { isMoney, type Money } from '@nucleus-commerce/core';

import { parseWhole, rateFieldsFor } from './rate-rules';

const moneyValue = z.custom<Money>(isMoney).nullable();
const whole = z
  .string()
  .trim()
  .refine((v) => v === '' || /^\d+$/.test(v), { error: 'shipping:rate.validation.wholeNumber' });

/** One schema for every kind; `superRefine` applies the checks the chosen kind needs. */
export const rateFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, { error: 'shipping:rate.validation.nameRequired' })
      .max(120, { error: 'shipping:rate.validation.nameLength' }),
    kind: z.enum(ShippingRateKind),
    price: moneyValue,
    minWeightGrams: whole,
    maxWeightGrams: whole,
    minSubtotal: moneyValue,
    maxSubtotal: moneyValue,
    deliveryDaysMin: whole,
    deliveryDaysMax: whole,
    requiresSignature: z.boolean(),
    isActive: z.boolean(),
    position: z.coerce
      .number<string | number>()
      .int({ error: 'shipping:rate.validation.position' })
      .min(0, { error: 'shipping:rate.validation.position' }),
  })
  .superRefine((v, ctx) => {
    const fields = rateFieldsFor(v.kind);
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });

    if (fields.price) {
      if (v.price === null) issue('price', 'shipping:rate.validation.priceRequired');
      else if (v.price.amount < 0) issue('price', 'shipping:rate.validation.priceNegative');
    }
    if (fields.weightBand) {
      const min = parseWhole(v.minWeightGrams);
      const max = parseWhole(v.maxWeightGrams);
      if (min === null && max === null) {
        issue('minWeightGrams', 'shipping:rate.validation.weightBandRequired');
      } else if (min !== null && max !== null && min > max) {
        issue('minWeightGrams', 'shipping:rate.validation.weightBandOrder');
      }
    }
    if (fields.subtotalBand) {
      if (v.minSubtotal === null && v.maxSubtotal === null) {
        issue('minSubtotal', 'shipping:rate.validation.subtotalBandRequired');
      } else if (v.minSubtotal && v.maxSubtotal && v.minSubtotal.amount > v.maxSubtotal.amount) {
        issue('minSubtotal', 'shipping:rate.validation.subtotalBandOrder');
      }
    }
    if (fields.threshold && v.minSubtotal === null) {
      issue('minSubtotal', 'shipping:rate.validation.thresholdRequired');
    }
    const dMin = parseWhole(v.deliveryDaysMin);
    const dMax = parseWhole(v.deliveryDaysMax);
    if (dMin !== null && dMax !== null && dMin > dMax) {
      issue('deliveryDaysMin', 'shipping:rate.validation.deliveryOrder');
    }
  });

export type RateFormInput = z.input<typeof rateFormSchema>;
export type RateFormOutput = z.output<typeof rateFormSchema>;
